<template>
  <div
    id="command-palette"
    class="command-palette"
    :hidden="!isOpen"
    :aria-hidden="!isOpen"
    role="dialog"
    aria-modal="true"
    aria-labelledby="command-title"
  >
    <div class="command-backdrop" @click="close"></div>
    <div class="command-container">
      <div class="command-header">
        <h2 id="command-title" class="command-title">Quick navigator</h2>
        <button
          type="button"
          class="command-close"
          data-command-close
          aria-label="Close command palette"
          @click="close"
        >
          ✕
        </button>
      </div>
      <div class="command-body">
        <input
          id="command-input"
          ref="inputRef"
          v-model="query"
          type="text"
          class="command-input"
          placeholder="Search commands..."
          role="combobox"
          aria-autocomplete="list"
          aria-controls="command-results"
          :aria-expanded="isOpen"
          @input="onInput"
        />
        <ul
          id="command-results"
          class="command-results"
          role="listbox"
        >
          <li
            v-if="filteredCommands.length === 0"
            class="command-empty"
          >
            No results found
          </li>
          <li
            v-for="(cmd, index) in filteredCommands"
            :key="index"
            class="command-result"
            :class="{ selected: index === selectedIndex }"
            role="option"
            :data-index="index"
            :aria-selected="index === selectedIndex"
            @click="execute(cmd)"
            @mouseenter="select(index)"
          >
            <span class="command-icon">{{ cmd.type === 'section' ? '→' : '⚡' }}</span>
            <span class="command-label">{{ cmd.label }}</span>
            <span class="command-type">{{ cmd.type }}</span>
          </li>
        </ul>
      </div>
    </div>
    <div
      id="command-announcer"
      class="sr-only"
      aria-live="polite"
      aria-atomic="true"
    >
      {{ announcement }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useAppStore } from '@/stores/app';

const store = useAppStore();

// Refs
const isOpen = ref(false);
const query = ref('');
const selectedIndex = ref(-1);
const inputRef = ref(null);
const announcement = ref('');

// Commands definition
const commands = ref([
  {
    type: 'section',
    label: 'What we do',
    href: '#what',
    keywords: ['placement', 'advisory', 'lp coverage', 'services']
  },
  {
    type: 'section',
    label: 'How we work',
    href: '#how',
    keywords: ['selective', 'methodical', 'aligned', 'process']
  },
  {
    type: 'section',
    label: 'Focus & edge',
    href: '#focus',
    keywords: ['private credit', 'insurance', 'ils', 'special situations']
  },
  {
    type: 'section',
    label: 'Experience',
    href: '#globe-window',
    keywords: ['globe', 'visualization', 'track record', 'stats']
  },
  {
    type: 'section',
    label: 'Contact',
    href: '#contact',
    keywords: ['email', 'get in touch', 'locations']
  },
  {
    type: 'action',
    label: 'Toggle globe view',
    action: () => {
      store.toggleGlobe();
    },
    keywords: ['globe', 'visualization', '3d']
  },
  {
    type: 'action',
    label: 'Toggle experience mode',
    action: () => {
      store.toggleFlows();
    },
    keywords: ['experience', 'flows', 'stats']
  },
  {
    type: 'action',
    label: 'Scroll to top',
    action: () => window.scrollTo({ top: 0, behavior: 'smooth' }),
    keywords: ['top', 'home', 'beginning']
  }
]);

// Computed
const filteredCommands = computed(() => {
  const lowerQuery = query.value.toLowerCase().trim();

  if (!lowerQuery) {
    return commands.value;
  }

  return commands.value.filter(cmd => {
    const labelMatch = cmd.label.toLowerCase().includes(lowerQuery);
    const keywordMatch = cmd.keywords.some(kw => kw.includes(lowerQuery));
    return labelMatch || keywordMatch;
  });
});

// Methods
function announce(message) {
  announcement.value = message;
  // Clear after screen readers have time to read
  setTimeout(() => {
    announcement.value = '';
  }, 1000);
}

function open() {
  if (isOpen.value) return;
  isOpen.value = true;
  document.body.style.overflow = 'hidden';

  nextTick(() => {
    inputRef.value?.focus();
  });

  announce('Command palette opened');
}

function close() {
  if (!isOpen.value) return;
  isOpen.value = false;
  document.body.style.overflow = '';
  query.value = '';
  selectedIndex.value = -1;
  announce('Command palette closed');
}

function select(index) {
  selectedIndex.value = index;
}

function execute(cmd) {
  if (cmd.href) {
    window.location.hash = cmd.href;
    close();
    announce(`Navigated to ${cmd.label}`);
  } else if (cmd.action) {
    cmd.action();
    close();
    announce(`Executed: ${cmd.label}`);
  }
}

function onInput() {
  selectedIndex.value = filteredCommands.value.length > 0 ? 0 : -1;
}

function scrollSelectedIntoView() {
  const selected = document.querySelector('.command-result.selected');
  if (selected) {
    selected.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }
}

function handleKeydown(e) {
  // Open with Cmd/Ctrl+K or Ctrl+Shift+P
  if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
    e.preventDefault();
    open();
    return;
  }

  if (e.ctrlKey && e.shiftKey && e.key === 'P') {
    e.preventDefault();
    open();
    return;
  }

  // Close with Escape
  if (isOpen.value && e.key === 'Escape') {
    e.preventDefault();
    close();
    return;
  }

  // Navigation within palette
  if (isOpen.value && filteredCommands.value.length > 0) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      selectedIndex.value = (selectedIndex.value + 1) % filteredCommands.value.length;
      scrollSelectedIntoView();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      selectedIndex.value = selectedIndex.value <= 0
        ? filteredCommands.value.length - 1
        : selectedIndex.value - 1;
      scrollSelectedIntoView();
    } else if (e.key === 'Enter' && selectedIndex.value >= 0) {
      e.preventDefault();
      execute(filteredCommands.value[selectedIndex.value]);
    }
  }
}

// Lifecycle
onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
  console.log('✓ Command palette (Vue) initialized');
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
  document.body.style.overflow = '';
});

// Expose methods for external access
defineExpose({
  open,
  close,
  isOpen: () => isOpen.value
});
</script>

<style scoped>
/* Command palette styles are inherited from global styles.css */
/* Component-specific overrides can go here if needed */
</style>
